import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from "react-tooltip";
import 'react-tooltip/dist/react-tooltip.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import serverURL from '../config';
import RetestStatusBadge from './RetestStatusBadge';
import { getCandidateStatusLabel, toNumber } from '../utils/reportUtils';

const CandidatesTable = ({ candidates, passPercentage = 92, eventId }) => {
    const navigate = useNavigate();
    const hasProgramming = candidates.some((candidate) => toNumber(candidate.programming_points) > 0);

    const handleRowClick = async (candidateId, candidateName) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${serverURL}/reports/results/${candidateId}/${eventId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            navigate('/report-details', {
                state: {
                    data: response.data,
                    candidateId,
                    candidateName,
                }
            });
        } catch (error) {
            console.error('Error fetching report details:', error);
        }
    };

    return (
        <div className="overflow-x-auto rounded-2xl shadow-card border border-campus-border bg-campus-surface">
            <table className="min-w-full text-left border-collapse">
                <thead>
                    <tr className="bg-campus-bg border-b border-campus-border text-campus-text-primary text-sm uppercase tracking-wider">
                        <th className="p-4 font-heading font-semibold whitespace-nowrap text-center">Rank</th>
                        <th className="p-4 font-heading font-semibold whitespace-nowrap text-center">Employee ID</th>
                        <th className="p-4 font-heading font-semibold">Candidate Name</th>
                        <th className="p-4 font-heading font-semibold">Email</th>
                        <th className="p-4 font-heading font-semibold whitespace-nowrap">Total Correct</th>
                        <th className="p-4 font-heading font-semibold whitespace-nowrap">Total Questions</th>
                        {hasProgramming && (
                            <th className="p-4 font-heading font-semibold whitespace-nowrap">Programming Points</th>
                        )}
                        <th className="p-4 font-heading font-semibold whitespace-nowrap">Status</th>
                        <th className="p-4 font-heading font-semibold whitespace-nowrap">Retest</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-campus-border bg-campus-surface">
                    {candidates.map((candidate, index) => {
                        const statusLabel = getCandidateStatusLabel(candidate, passPercentage);
                       let statusClasses = 'bg-amber-50 text-amber-700 border-amber-200';

if (statusLabel === 'PASS') {
    statusClasses = 'bg-green-50 text-campus-success border-green-200';
} else if (statusLabel === 'FAIL') {
    statusClasses = 'bg-red-50 text-campus-error border-red-200';
}

                        return (
                            <tr
                                key={candidate.candidate_id}
                                className="cursor-pointer hover:bg-campus-bg transition-colors duration-200"
                                onClick={() => handleRowClick(candidate.candidate_id, candidate.candidate_name)}
                                data-tooltip-id="row-tooltip"
                                data-tooltip-content="Click to view detailed report"
                            >
                                <td className="p-4 text-campus-text-body align-middle text-center">{index + 1}</td>
                                <td className="p-4 text-center font-mono text-sm align-middle">
                                    {candidate.employee_id
                                        ? <span className="bg-campus-bg border border-campus-border text-campus-text-primary px-2 py-1 rounded-md">{candidate.employee_id}</span>
                                        : <span className="text-campus-text-body">&mdash;</span>}
                                </td>
                                <td className="p-4 font-medium text-campus-text-primary align-middle">{candidate.candidate_name}</td>
                                <td className="p-4 text-campus-text-body align-middle">{candidate.candidate_email}</td>
                                <td className="p-4 text-campus-text-primary font-medium align-middle">{candidate.total_correct}</td>
                                <td className="p-4 text-campus-text-body align-middle">{candidate.total_attempted}</td>
                                {hasProgramming && (
                                    <td className="p-4 text-campus-text-primary align-middle">{candidate.programming_points}</td>
                                )}
                                <td className="p-4 align-middle">
                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${statusClasses}`}>
                                        {statusLabel}
                                    </span>
                                </td>
                                <td className="p-4 align-middle">
                                    <RetestStatusBadge status={candidate.retest_status} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <Tooltip id="row-tooltip" />
        </div>
    );
};

CandidatesTable.propTypes = {
    candidates: PropTypes.arrayOf(PropTypes.shape({
        candidate_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        candidate_name: PropTypes.string,
        candidate_email: PropTypes.string,
        employee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        total_correct: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        total_attempted: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        programming_points: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        passed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        final_result: PropTypes.string,
        retest_status: PropTypes.string,
    })).isRequired,
    passPercentage: PropTypes.number,
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CandidatesTable;
